import { useEffect, useRef, useState } from 'react';
import {
  Bold,
  Heading2,
  Image,
  Italic,
  Link,
  List,
  ListOrdered,
  Quote,
  Underline,
  Undo2
} from 'lucide-react';

const toolbarButtonClass = 'rounded-md p-2 text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50';
const mobileImageStyle = 'width: 100%; max-width: 100%; height: auto; display: block;';

const escapeAttribute = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/"/g, '&quot;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const RichTextEditor = ({
  value,
  onChange,
  onUploadImage,
  onUploadError,
  placeholder = 'Escribe el contenido...'
}) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const selectionRef = useRef(null);
  const lastValueRef = useRef('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!editorRef.current) return;

    const nextValue = value || '';
    if (editorRef.current.innerHTML !== nextValue) {
      editorRef.current.innerHTML = nextValue;
    }

    lastValueRef.current = nextValue;
  }, [value]);

  const syncValue = () => {
    const html = editorRef.current?.innerHTML || '';
    lastValueRef.current = html;
    onChange(html);
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editorRef.current) return;

    const range = selection.getRangeAt(0);
    if (editorRef.current.contains(range.commonAncestorContainer)) {
      selectionRef.current = range;
    }
  };

  const restoreSelection = () => {
    if (!editorRef.current) return;
    editorRef.current.focus();

    if (!selectionRef.current) return;

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(selectionRef.current);
  };

  const runCommand = (command, commandValue = null) => {
    restoreSelection();
    document.execCommand(command, false, commandValue);
    syncValue();
    saveSelection();
  };

  const insertHtml = (html) => {
    restoreSelection();
    document.execCommand('insertHTML', false, html);
    syncValue();
    saveSelection();
  };

  const handleLink = () => {
    saveSelection();
    const url = window.prompt('URL del enlace');
    if (!url) return;
    runCommand('createLink', url);
  };

  const handleImageClick = () => {
    saveSelection();
    fileInputRef.current?.click();
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file || !onUploadImage) return;

    try {
      setUploading(true);
      const url = await onUploadImage(file);
      if (url) {
        insertHtml(`<img src="${escapeAttribute(url)}" alt="${escapeAttribute(file.name)}" style="${mobileImageStyle}" />`);
      }
    } catch (error) {
      onUploadError?.(error);
    } finally {
      setUploading(false);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const text = event.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    syncValue();
  };

  const stopMouseDown = (event) => event.preventDefault();

  const toolbarItems = [
    { title: 'Negrita', icon: Bold, action: () => runCommand('bold') },
    { title: 'Cursiva', icon: Italic, action: () => runCommand('italic') },
    { title: 'Subrayado', icon: Underline, action: () => runCommand('underline') },
    { title: 'Titulo', icon: Heading2, action: () => runCommand('formatBlock', 'h2') },
    { title: 'Cita', icon: Quote, action: () => runCommand('formatBlock', 'blockquote') },
    { title: 'Lista', icon: List, action: () => runCommand('insertUnorderedList') },
    { title: 'Lista numerada', icon: ListOrdered, action: () => runCommand('insertOrderedList') },
    { title: 'Enlace', icon: Link, action: handleLink },
    { title: 'Imagen', icon: Image, action: handleImageClick, disabled: uploading },
    { title: 'Deshacer', icon: Undo2, action: () => runCommand('undo') }
  ];

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 bg-gray-50 px-2 py-2">
        {toolbarItems.map((item) => (
          <button
            key={item.title}
            type="button"
            title={item.title}
            className={toolbarButtonClass}
            onMouseDown={stopMouseDown}
            onClick={item.action}
            disabled={item.disabled}
          >
            <item.icon className="h-4 w-4" />
          </button>
        ))}
        {uploading && <span className="ml-2 text-xs text-gray-500">Subiendo imagen...</span>}
      </div>

      <div className="relative">
        {!value && (
          <span className="pointer-events-none absolute left-4 top-4 text-sm text-gray-400">
            {placeholder}
          </span>
        )}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          className="min-h-[340px] overflow-y-auto px-4 py-4 text-sm leading-6 text-gray-900 outline-none [&_a]:text-blue-700 [&_blockquote]:border-l-4 [&_blockquote]:border-blue-200 [&_blockquote]:pl-3 [&_blockquote]:text-gray-600 [&_h2]:mb-3 [&_h2]:mt-4 [&_h2]:text-xl [&_h2]:font-bold [&_img]:my-4 [&_img]:max-h-96 [&_img]:w-full [&_img]:rounded-lg [&_img]:object-cover [&_li]:ml-5 [&_ol]:list-decimal [&_p]:mb-3 [&_strong]:font-semibold [&_ul]:list-disc"
          onInput={syncValue}
          onBlur={saveSelection}
          onKeyUp={saveSelection}
          onMouseUp={saveSelection}
          onPaste={handlePaste}
        />
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleImageChange}
      />
    </div>
  );
};

export default RichTextEditor;
